import { useNavigate, useParams } from "react-router-dom";
import AvatarInterface from "./AvatarInterface";
import { useProfileData } from "../../hooks/useProfileData";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ConversationType, startConversation } from "../../utils/avatar_api";
import { DailyProvider } from "@daily-co/daily-react";
import { useAuth } from "../../hooks/useAuth";
import { USER_TYPE } from "../../config/const";
import { logging } from "../../utils/logger";
import { toast } from "react-toastify";

const AvatarContainer: React.FC = () => {
  const [ loaded, setLoaded ] = useState<boolean>(false);
  const [ conversation, setConversation ] = useState<ConversationType | null>(null);
  const { userName: userNameParam } = useParams();
  const { authUser, authLoading } = useAuth();
  const userName = userNameParam?.toLowerCase();
  const navigate = useNavigate();

  const {
    fetchProfileDataFromUserName,
    creatorProfileData
  } = useProfileData();

  const hasLoadedCreatorProfile: boolean = useMemo(() => {
    return (
      (creatorProfileData?.userName || "").toLowerCase() === userName?.toLowerCase() &&
      !!creatorProfileData?.userId
    );
  }, [creatorProfileData, userName]);

  useEffect(() => {
    if (!loaded && !authLoading) {
      if (userName && !hasLoadedCreatorProfile) {
        fetchProfileDataFromUserName(userName, authUser?.uid, true,
          () => {navigate(`/${userName}/chat`)})
          .then(() => {
            if (creatorProfileData && !creatorProfileData.avatar_data) {
              navigate(`/${userName}/chat`);
            } else {
              if (creatorProfileData) {
                setLoaded(true);
              }
            }
          })
          .catch((error) => {
            logging.error("Error fetching profile data: ", {context: {error}});
            toast.error("Error loading avatar. Please try again later.");
            navigate(`/${userName}/chat`);
          });
      } else if (hasLoadedCreatorProfile) {
        setLoaded(true);
      }
    }
  }, [
    userName, creatorProfileData, loaded, authLoading,
    fetchProfileDataFromUserName, authUser, hasLoadedCreatorProfile, navigate
  ])

  const returnRoomId = useCallback(() => {
    if (authUser?.uid) {
      if (
        authUser?.uid === creatorProfileData?.userId
      ) {
        return `${authUser?.uid}.${USER_TYPE.BOT}`;
      } else {
        return `${creatorProfileData?.userId}.${authUser?.uid}`;
      }
    } else {
      return `${creatorProfileData?.userId}.${USER_TYPE.ANONYMOUS}`;
    }
  }, [authUser?.uid, creatorProfileData?.userId]);

  const createConversation = useCallback(async () => {
    logging.info(`Starting conversation with ${creatorProfileData?.userId || ""}`)
    let roomId = returnRoomId();
    startConversation(roomId || "")
      .then((conversationData: ConversationType | null) => {
        if (!conversationData || Object.keys(conversationData).length === 0) {
          logging.error("Error starting avatar conversation", {context: {conversationData, roomId}})
          toast.error("Error starting 3D avatar conversation. Please try again later.")
          navigate(`/${userName}/chat`);
        } else {
          setConversation(conversationData)
        }
      })
  }, [creatorProfileData, navigate, userName, returnRoomId]);

  useEffect(() => {
    if (loaded) {
      createConversation();
    }
  }, [loaded, createConversation])

  return (
    (loaded) ?
      <DailyProvider>
        <AvatarInterface conversation={conversation}/>
      </DailyProvider> : null
  )
}

export default AvatarContainer;
